import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Edit2,
  Copy,
  Trash2,
  Trash,
  Plus,
  X,
  Save,
  FolderInput,
  StickyNote,
  Layers,
} from "lucide-react";
import { articlesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import Spinner from "../components/Spinner";
import { formatReadingEstimate } from "../utils/readingTime";
import "./StudyLessons.css";

const AllLessons = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState(null);
  const [newName, setNewName] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const [movingLessonId, setMovingLessonId] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);
  const { addToast } = useToasts();

  const handleCreateLesson = () => {
    navigate("/content-lesson");
  };

  const loadLessons = async () => {
    try {
      const response = await articlesAPI.getMyArticles("lesson", true);
      const allLessons = response.data.articles
        .map((lesson) => ({
          _id: lesson._id,
          title: lesson.title,
          chapter: lesson.chapter,
          author: lesson.author,
          createdAt: lesson.createdAt,
          contentType: lesson.contentType,
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setLessons(allLessons);
      setLoading(false);
    } catch {
      addToast("Failed to load lessons", "error-warning");
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLessons();
  }, []);

  const handleCopy = async (article) => {
    try {
      // Load full article when copying
      const response = await articlesAPI.getArticleById(article._id);
      await navigator.clipboard.writeText(response.data.article.body);
      addToast("Lesson copied to clipboard", "success-info");
    } catch {
      addToast("Failed to copy to clipboard", "error-warning");
    }
  };

  const handleDelete = async (article) => {
    setDeletingId(null);
    setDeleteStep(0);
    try {
      const response = await articlesAPI.getMyArticles();
      const notesToDelete = response.data.articles.filter(
        (a) => a.lessonId === article._id,
      );

      await Promise.all([
        articlesAPI.deleteArticle(article._id),
        ...notesToDelete.map((note) => articlesAPI.deleteArticle(note._id)),
      ]);

      const count = notesToDelete.length;
      addToast(
        count > 0
          ? `Lesson and ${count} note${count > 1 ? "s" : ""} deleted`
          : "Lesson deleted successfully",
        "success-info",
      );
      loadLessons();
    } catch {
      addToast("Failed to delete lesson", "error-warning");
    }
  };

  const handleRename = async (article) => {
    if (!newName.trim()) {
      addToast("Name cannot be empty", "error-warning");
      return;
    }
    try {
      const response = await articlesAPI.getArticleById(article._id);
      const fullArticle = response.data.article;
      await articlesAPI.updateArticle(article._id, {
        title: fullArticle.title,
        author: fullArticle.author,
        chapter: newName.trim(),
        body: fullArticle.body,
      });
      addToast("Lesson renamed successfully", "success-info");
      setRenamingId(null);
      setNewName("");
      loadLessons();
    } catch {
      addToast("Failed to rename lesson", "error-warning");
    }
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async (article) => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    if (deleteStep === 2) {
      await handleDelete(article);
    }
  };

  const handleDeleteCancel = () => {
    setDeletingId(null);
    setDeleteStep(0);
  };

  const handleMoveLessonClick = async (e, lessonId) => {
    e.stopPropagation();
    try {
      const response = await articlesAPI.getFolders("lesson");
      const allFolders = response.data.folders;
      const leafFolders = allFolders.filter((folder) => {
        const hasSubfolder = allFolders.some((f) =>
          f.name.startsWith(folder.name + " / "),
        );
        return !hasSubfolder;
      });
      setAvailableFolders(leafFolders);
      setMovingLessonId(lessonId);
    } catch {
      addToast("Failed to load folders", "error-warning");
    }
  };

  const handleMoveLesson = async (article, targetFolder) => {
    try {
      const response = await articlesAPI.getArticleById(article._id);
      const fullArticle = response.data.article;
      await articlesAPI.updateArticle(article._id, {
        title: targetFolder,
        author: fullArticle.author,
        chapter: fullArticle.chapter,
        body: fullArticle.body,
      });
      addToast(`Lesson moved to "${targetFolder}"`, "success-info");
      setMovingLessonId(null);
      setAvailableFolders([]);
      loadLessons();
    } catch {
      addToast("Failed to move lesson", "error-warning");
    }
  };

  const buildBreadcrumbs = () => {
    return [
      { label: "MySpace", path: "/myspace" },
      { label: "All Lessons", path: null },
    ];
  };

  if (loading) {
    return (
      <StudyLayout breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}>
        <Spinner visible={true} />
      </StudyLayout>
    );
  }

  return (
    <StudyLayout breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}>
      <div className="study-lessons-grid">
        {lessons.map((article, index) => (
          <div
            key={article._id}
            className="study-lesson-card"
            onClick={() =>
              navigate(
                `/view/lesson/${article._id}?folder=${encodeURIComponent(article.title)}`,
              )
            }
          >
            <div
              className="study-lesson-btn"
              style={{
                position: "absolute",
                top: "6px",
                left: "12px",
                cursor: "pointer",
                color: "#475569",
                padding: "4px",
                borderRadius: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/view/lesson/${article._id}?folder=${encodeURIComponent(article.title)}`,
                );
              }}
              title="View lesson"
            >
              <BookOpen size={32} />
            </div>
            <div
              className="study-lesson-btn"
              style={{
                position: "absolute",
                top: "12px",
                left: "56px",
                cursor: "pointer",
                color: "#3b82f6",
                padding: "4px",
                borderRadius: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/study/notes?folder=${encodeURIComponent(article.title)}&type=note&lessonId=${article._id}`,
                );
              }}
              title="View notes"
            >
              <StickyNote size={20} />
            </div>
            <div
              className="study-lesson-btn"
              style={{
                position: "absolute",
                top: "12px",
                left: "92px",
                cursor: "pointer",
                color: "#f59e0b",
                padding: "4px",
                borderRadius: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/study/flashcards?folder=${encodeURIComponent(article.title)}&lessonId=${article._id}`,
                );
              }}
              title="View flashcards"
            >
              <Layers size={20} />
            </div>
            <div style={{ height: "32px" }}></div>
            <div>
              {renamingId === article._id ? (
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRename(article);
                    if (e.key === "Escape") {
                      setRenamingId(null);
                      setNewName("");
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  className="folder-name-input"
                />
              ) : (
                <div className="study-lesson-name">
                  {article.chapter || `Lesson ${index + 1}`}
                </div>
              )}
            </div>
            <div
              className="study-lesson-info"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="study-lesson-count">
                {formatReadingEstimate(article)}
              </div>
              <div className="study-lesson-actions">
                <button
                  className="study-lesson-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveLessonClick(e, article._id);
                  }}
                  title="Move lesson to another folder"
                >
                  <FolderInput size={18} />
                </button>
                <button
                  className={`study-lesson-btn ${renamingId === article._id ? "save-mode" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (renamingId === article._id) {
                      handleRename(article);
                    } else {
                      setRenamingId(article._id);
                      setNewName(article.chapter || `Lesson ${index + 1}`);
                    }
                  }}
                  title={
                    renamingId === article._id
                      ? "Save changes"
                      : "Rename lesson"
                  }
                >
                  {renamingId === article._id ? (
                    <Save size={18} />
                  ) : (
                    <Edit2 size={18} />
                  )}
                </button>
                <button
                  className="study-lesson-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(article);
                  }}
                  title="Copy lesson"
                >
                  <Copy size={18} />
                </button>
                <button
                  className="study-lesson-delete-btn"
                  onClick={(e) => handleDeleteClick(e, article._id)}
                  title="Delete lesson"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>

            {deleteStep > 0 && deletingId === article._id && (
              <div
                className="delete-confirmation-in-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="delete-confirmation-content">
                  <div className="delete-text">
                    {deleteStep === 1
                      ? `Delete lesson?`
                      : "Delete? Cannot be undone!"}
                  </div>
                  <div className="delete-actions">
                    <button
                      type="button"
                      className="delete-confirm-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConfirm(article);
                      }}
                      title={deleteStep === 1 ? "Delete" : "Yes, Delete"}
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="delete-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCancel();
                      }}
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {movingLessonId === article._id && (
              <div
                className="subfolder-create-in-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="delete-confirmation-content"
                  style={{ gap: "4px" }}
                >
                  {availableFolders.length === 0 ? (
                    <div
                      style={{
                        padding: "8px",
                        fontSize: "0.75rem",
                        color: "var(--color-text-primary)",
                        textAlign: "center",
                      }}
                    >
                      No folders available to move lesson to.
                    </div>
                  ) : (
                    <div
                      style={{
                        maxHeight: "100px",
                        overflowY: "auto",
                        width: "100%",
                        fontSize: "0.75rem",
                      }}
                    >
                      {availableFolders.map((folder) => (
                        <div
                          key={folder.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveLesson(article, folder.name);
                          }}
                          style={{
                            padding: "4px 8px",
                            cursor: "pointer",
                            borderBottom: "1px solid var(--color-border)",
                            color: "var(--color-text-primary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.background = "var(--color-hover)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.background = "transparent")
                          }
                          title={folder.name}
                        >
                          {folder.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className="delete-cancel-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMovingLessonId(null);
                      setAvailableFolders([]);
                    }}
                    title="Cancel"
                    style={{ alignSelf: "center" }}
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <div
          className="study-lesson-card study-create-card"
          onClick={handleCreateLesson}
          title="Create new lesson"
          style={{ position: "relative" }}
        >
          <BookOpen
            size={32}
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              opacity: 0.6,
            }}
          />
          <Plus size={48} />
        </div>
      </div>
    </StudyLayout>
  );
};

export default AllLessons;
